/**
 * SEO utilities for managing meta tags across the website
 * This file now acts as a wrapper for the specialized SEO utility modules
 */

import { updateMetaTags as coreUpdateMetaTags, getCanonicalUrl } from './seoCore';
import { updateOpenGraphTags } from './seoSocial';
import { updateLanguageAlternateLinks } from './seoMultilingual';
import { generateArticleSchema, formatSchemaDate } from './seoSchema';

interface MetaTagsProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  language?: string;
  schemaType?: string;
  businessType?: string;
}

/**
 * Updates the meta tags in the document head based on provided values
 * This is a wrapper around the specialized functions for backward compatibility
 */
export const updateMetaTags = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = "https://future-level.ch/opengraph-image-p98pqg.png", // Default OG image
  ogType = "website",
  language = "de", // Default language
  schemaType,
  businessType
}: MetaTagsProps): void => {
  // Update core meta tags
  coreUpdateMetaTags({ title, description, keywords, canonicalUrl, ogImage, ogType, language });
  
  // Add language alternate links for SEO
  updateLanguageAlternateLinks(canonicalUrl, language);

  // Update social media tags
  updateOpenGraphTags(title, description, ogImage, ogType, canonicalUrl, language);
  
  // Add theme-color meta tag for mobile browsers
  let themeColor = document.querySelector('meta[name="theme-color"]');
  if (!themeColor) {
    themeColor = document.createElement('meta');
    themeColor.setAttribute('name', 'theme-color');
    document.head.appendChild(themeColor);
  }
  themeColor.setAttribute('content', '#6D28D9'); // Purple color matching the site theme
  
  // Add schema markup if specified
  if (schemaType && schemaType === "ContactPage") {
    addContactPageSchema(title, description, canonicalUrl || "", language, businessType || "OnlineService");
  }
};

/**
 * Adds ContactPage and VirtualLocation schema markup to the page
 * Indicates that this is an online business with a registration address only
 */
export const addContactPageSchema = (
  title: string,
  description: string,
  url: string,
  language: string,
  businessType: string = "OnlineService"
): void => {
  // Create LocalBusiness schema that specifies it's a virtual business
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": ["Organization", businessType],
    "name": "Future Level",
    "description": description,
    "url": url,
    "logo": "https://future-level.ch/logo.png",
    "email": "info@future-level.ch",
    "telephone": "+41 71 539 24 24",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Meilipromenade 14",
      "addressLocality": "Emmen",
      "postalCode": "6032",
      "addressCountry": "CH",
      "addressType": "RegisteredAddress" // Custom property to indicate this is a registered address only
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "info@future-level.ch",
      "availableLanguage": [
        {
          "@type": "Language",
          "name": language === "de" ? "German" : language === "en" ? "English" : 
                 language === "fr" ? "French" : "Italian"
        }
      ]
    },
    "sameAs": [
      "https://www.linkedin.com/company/future-level",
      "https://www.instagram.com/futurelevel.ch/"
    ]
  };
  
  // Create ContactPage schema
  const contactPageSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": title,
    "description": description,
    "url": url,
    "mainEntity": {
      "@type": "Organization",
      "name": "Future Level",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "email": "info@future-level.ch",
        "availableLanguage": [
          language === "de" ? "German" : language === "en" ? "English" : 
          language === "fr" ? "French" : "Italian"
        ]
      }
    }
  };
  
  // Add schemas to the page
  const scripts = document.querySelectorAll('script[type="application/ld+json"]');
  scripts.forEach(script => {
    // Remove any existing schema of these types
    const scriptContent = JSON.parse(script.textContent || "{}");
    if (scriptContent["@type"] === "ContactPage" || 
        scriptContent["@type"] === "Organization" || 
        scriptContent["@type"] === "LocalBusiness") {
      script.remove();
    }
  });
  
  // Add LocalBusiness schema
  let businessScriptTag = document.createElement('script');
  businessScriptTag.setAttribute('type', 'application/ld+json');
  businessScriptTag.textContent = JSON.stringify(localBusinessSchema);
  document.head.appendChild(businessScriptTag);
  
  // Add ContactPage schema
  let contactScriptTag = document.createElement('script');
  contactScriptTag.setAttribute('type', 'application/ld+json');
  contactScriptTag.textContent = JSON.stringify(contactPageSchema);
  document.head.appendChild(contactScriptTag);
  
  // Add breadcrumb schema
  addBreadcrumbSchema(url, title, language);
};

/**
 * Adds BreadcrumbList schema markup for better navigation structure
 */
export const addBreadcrumbSchema = (url: string, pageTitle: string, language: string): void => {
  const homeLabel = language === "de" ? "Startseite" : 
                   language === "en" ? "Home" : 
                   language === "fr" ? "Accueil" : "Home";
  
  const contactLabel = language === "de" ? "Kontakt" : 
                      language === "en" ? "Contact" : 
                      language === "fr" ? "Contact" : "Contatto";
  
  const baseUrl = 'https://future-level.ch';
  
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": homeLabel,
        "item": `${baseUrl}/${language}`
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": contactLabel,
        "item": url
      }
    ]
  };
  
  // Add breadcrumb schema
  let breadcrumbScriptTag = document.createElement('script');
  breadcrumbScriptTag.setAttribute('type', 'application/ld+json');
  breadcrumbScriptTag.textContent = JSON.stringify(breadcrumbSchema);
  document.head.appendChild(breadcrumbScriptTag);
};

// Export all SEO utilities for backward compatibility
export { 
  getCanonicalUrl,
  generateArticleSchema,
  formatSchemaDate 
};
